import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, X, Filter, ChevronRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';

// Hub data organized by platform
const hubsByPlatform = {
  apple: {
    name: 'Apple Home',
    hubs: [
      { name: 'HomePod Mini', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
      { name: 'HomePod (2nd gen)', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
      { name: 'Apple TV 4K (2nd gen+)', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
      { name: 'iPad (always home)', matter: true, thread: false, wifi: true, notes: 'Wi-Fi only, no Thread' },
    ],
  },
  google: {
    name: 'Google Home',
    hubs: [
      { name: 'Nest Hub (2nd gen)', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
      { name: 'Nest Hub Max', matter: true, thread: false, wifi: true, notes: 'Wi-Fi only' },
      { name: 'Nest Wifi Pro', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
      { name: 'Nest Audio', matter: true, thread: false, wifi: true, notes: 'Wi-Fi only' },
    ],
  },
  amazon: {
    name: 'Amazon Alexa',
    hubs: [
      { name: 'Echo (4th gen)', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
      { name: 'Echo Show 10 (3rd gen)', matter: true, thread: false, wifi: true, notes: 'Wi-Fi only' },
      { name: 'Echo Studio', matter: true, thread: false, wifi: true, notes: 'Wi-Fi only' },
      { name: 'eero 6+', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
    ],
  },
  samsung: {
    name: 'Samsung SmartThings',
    hubs: [
      { name: 'SmartThings Station', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
      { name: 'SmartThings Hub v3', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
      { name: 'SmartThings Hub v2', matter: false, thread: false, wifi: true, notes: 'Legacy, Wi-Fi bridge only' },
      { name: 'Aeotec Smart Home Hub', matter: true, thread: true, wifi: true, notes: 'Thread border router' },
    ],
  },
};

// Bridges
const bridges = [
  {
    name: 'Motion Blinds Wi-Fi Bridge (CM-55)',
    description: 'Connect up to 25 motors to Wi-Fi and Matter. Required for non-Thread motors.',
    features: ['Matter support', 'Up to 25 motors', 'Remote access', 'Voice control'],
    recommended: true,
  },
  {
    name: 'Motion Blinds Bridge (CM-20)',
    description: 'Basic Wi-Fi bridge for cloud and app control. No Matter support.',
    features: ['App control', 'Up to 25 motors', 'Remote access', 'Schedules'],
    recommended: false,
  },
];

export const HubsPage = () => {
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  const platforms = Object.keys(hubsByPlatform);

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-neutral-900 pt-32 pb-16" data-testid="hubs-hero">
        <div className="container-xl">
          <Link to="/motorization/smart-connectivity" className="inline-flex items-center text-neutral-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Smart Connectivity
          </Link>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-white text-4xl lg:text-5xl font-serif mb-4">Compatible Hubs</h1>
            <p className="text-xl text-neutral-300">
              Find the right hub or border router for your motorized shades. 
              Many devices you already own can work as a hub.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="sticky top-20 z-30 bg-white border-b border-neutral-200 py-4">
        <div className="container-xl">
          <div className="flex items-center gap-4 overflow-x-auto">
            <Filter className="w-4 h-4 text-neutral-400 flex-shrink-0" />
            <button
              onClick={() => setSelectedPlatform('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedPlatform === 'all' 
                  ? 'bg-neutral-900 text-white' 
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              All Platforms
            </button>
            {platforms.map((platform) => (
              <button
                key={platform}
                onClick={() => setSelectedPlatform(platform)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedPlatform === platform 
                    ? 'bg-neutral-900 text-white' 
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {hubsByPlatform[platform].name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Hubs List */}
      <section className="section-padding" data-testid="hubs-list">
        <div className="container-xl">
          <div className="space-y-12">
            {platforms
              .filter((platform) => selectedPlatform === 'all' || selectedPlatform === platform)
              .map((platform) => (
                <motion.div
                  key={platform}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h2 className="text-2xl font-semibold mb-6">{hubsByPlatform[platform].name}</h2>
                  <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                    <div className="hidden md:grid grid-cols-5 gap-4 p-4 bg-neutral-50 text-sm font-medium text-neutral-500">
                      <div>Device</div>
                      <div className="text-center">Matter</div>
                      <div className="text-center">Thread</div>
                      <div className="text-center">Wi-Fi Bridge</div>
                      <div>Notes</div>
                    </div>
                    {hubsByPlatform[platform].hubs.map((hub, index) => (
                      <div 
                        key={hub.name}
                        className={`grid md:grid-cols-5 gap-4 p-4 items-center ${
                          index !== hubsByPlatform[platform].hubs.length - 1 ? 'border-b border-neutral-100' : ''
                        }`}
                      >
                        <div className="font-medium">{hub.name}</div>
                        <div className="flex md:justify-center items-center gap-2">
                          <span className="md:hidden text-sm text-neutral-500">Matter:</span>
                          {hub.matter ? (
                            <Check className="w-5 h-5 text-green-500" />
                          ) : (
                            <X className="w-5 h-5 text-neutral-300" />
                          )}
                        </div>
                        <div className="flex md:justify-center items-center gap-2">
                          <span className="md:hidden text-sm text-neutral-500">Thread:</span>
                          {hub.thread ? (
                            <Check className="w-5 h-5 text-green-500" />
                          ) : (
                            <X className="w-5 h-5 text-neutral-300" />
                          )}
                        </div>
                        <div className="flex md:justify-center items-center gap-2">
                          <span className="md:hidden text-sm text-neutral-500">Wi-Fi:</span>
                          {hub.wifi ? (
                            <Check className="w-5 h-5 text-green-500" />
                          ) : (
                            <X className="w-5 h-5 text-neutral-300" />
                          )}
                        </div>
                        <div className="text-sm text-neutral-500">{hub.notes}</div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      {/* Bridges Section */}
      <section className="section-padding bg-neutral-50" data-testid="bridges-section">
        <div className="container-xl">
          <div className="text-center mb-12">
            <span className="label-overline text-amber-700">No Compatible Hub?</span>
            <h2 className="mt-4 text-3xl">Wi-Fi Bridges</h2>
            <p className="mt-4 text-neutral-600 max-w-2xl mx-auto">
              Connect your motors without a Thread border router using our Wi-Fi bridges.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {bridges.map((bridge) => (
              <div 
                key={bridge.name}
                className={`bg-white border rounded-xl p-6 ${
                  bridge.recommended ? 'border-amber-400 ring-1 ring-amber-400' : 'border-neutral-200'
                }`}
              >
                {bridge.recommended && (
                  <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full mb-4">
                    Recommended
                  </span>
                )}
                <h3 className="text-xl font-semibold mb-2">{bridge.name}</h3>
                <p className="text-neutral-600 mb-4">{bridge.description}</p>
                <ul className="space-y-2">
                  {bridge.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-xl text-center">
          <h2 className="text-3xl mb-4">Not Sure What You Need?</h2>
          <p className="text-neutral-600 max-w-xl mx-auto mb-8">
            Our team can help you choose the right connectivity setup for your home.
          </p>
          <Link to="/contact?consultation=true">
            <Button className="bg-neutral-900 hover:bg-neutral-800 text-white px-8">
              Get Expert Help
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default HubsPage;
